import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const features = [
  ['Billing Management', 'Create clear bills and keep client payments organized.'],
  ['Order Tracking', 'Follow every order from entry to delivery without guesswork.'],
  ['Multi-Order per Client', 'Handle multiple active jobs for the same client with ease.'],
  ['Image Upload with Orders', 'Attach product or reference images directly to orders.'],
  ['Auto Order ID Generation', 'Give each order a clean, trackable identity automatically.'],
  ['Manual Completion Tracking', 'Mark progress at the exact pace your workflow needs.'],
  ['Client-wise Billing', 'View client history and billing details in one place.'],
  ['Scalable & Fast', 'Built for growing shops that need speed without clutter.'],
];

const steps = [
  ['Add Orders', 'Capture client, order, image, and amount details.'],
  ['Track Progress', 'Move work through each stage as your team completes it.'],
  ['Generate Bills', 'Turn completed work into client-ready billing records.'],
  ['Manage Clients', 'Keep orders and billing history tied to each customer.'],
];

const benefits = [
  ['Save Time', 'Replace scattered notes with one focused workflow.'],
  ['Reduce Errors', 'Use consistent order IDs and billing records.'],
  ['Better Client Management', 'See client activity without digging through files.'],
  ['Organized Workflow', 'Know what is pending, completed, and ready to bill.'],
];

function IconBadge({ label }) {
  return (
    <span className="landing-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" role="img" focusable="false">
        <path d="M12 3.5 20.5 8v8L12 20.5 3.5 16V8L12 3.5Z" />
        <path d="M8.5 12.2 11 14.7l4.7-5.4" />
      </svg>
      <span className="visually-hidden">{label}</span>
    </span>
  );
}

function LandingPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <main className="landing-page">
      <nav className={`navbar navbar-expand-lg fixed-top landing-navbar ${isScrolled ? 'is-scrolled' : ''}`}>
        <div className="container">
          <Link className="navbar-brand fw-bold" to="/">
            Fintrix
          </Link>
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#landingNav"
            aria-controls="landingNav"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon" />
          </button>
          <div className="collapse navbar-collapse" id="landingNav">
            <ul className="navbar-nav mx-auto mb-2 mb-lg-0">
              <li className="nav-item">
                <a className="nav-link" href="#features">Features</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="#workflow">How it works</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="#preview">Preview</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="#benefits">Benefits</a>
              </li>
            </ul>
            <div className="d-flex gap-2">
              <Link className="btn btn-outline-primary" to="/login">Login</Link>
              <Link className="btn btn-primary" to="/register">Register</Link>
            </div>
          </div>
        </div>
      </nav>

      <section className="landing-hero d-flex align-items-center">
        <div className="container py-5">
          <div className="row align-items-center g-4 py-5">
            <div className="col-lg-6 reveal-up">
              <span className="badge rounded-pill text-bg-light border mb-3">Billing and order tracking for small businesses</span>
              <h1 className="display-4 fw-bold mb-3">Fintrix</h1>
              <p className="h3 fw-semibold mb-3">Smart Billing & Order Tracking Made Simple</p>
              <p className="lead text-secondary mb-4">
                Manage clients, orders, images, completion status, and generated bills from one fast, organized workspace.
              </p>
              <div className="d-flex flex-column flex-sm-row gap-3">
                <Link className="btn btn-primary btn-lg px-4" to="/register">Get Started</Link>
                <a className="btn btn-outline-dark btn-lg px-4" href="#preview">View Demo</a>
              </div>
            </div>
            <div className="col-lg-6 reveal-up">
              <div className="card border-0 shadow-lg hero-preview">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <p className="text-secondary mb-1">Today</p>
                      <h2 className="h4 fw-bold mb-0">Order Dashboard</h2>
                    </div>
                    <span className="badge text-bg-primary">Live</span>
                  </div>
                  <div className="row g-3 mb-4">
                    <div className="col-4">
                      <div className="preview-stat">
                        <span>Orders</span>
                        <strong>128</strong>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="preview-stat">
                        <span>Pending</span>
                        <strong>34</strong>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="preview-stat">
                        <span>Billed</span>
                        <strong>92</strong>
                      </div>
                    </div>
                  </div>
                  <div className="list-group list-group-flush">
                    {['Client order uploaded', 'Measurement checked', 'Bill ready for approval'].map((item, index) => (
                      <div className="list-group-item px-0 d-flex justify-content-between align-items-center" key={item}>
                        <span>{item}</span>
                        <span className="badge rounded-pill text-bg-light border">0{index + 1}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5" id="features">
        <div className="container py-4">
          <div className="text-center mx-auto mb-5 section-heading">
            <p className="text-primary fw-semibold mb-2">Features</p>
            <h2 className="fw-bold">Everything needed to run order-heavy billing</h2>
          </div>
          <div className="row g-4">
            {features.map(([title, description]) => (
              <div className="col-sm-6 col-lg-3" key={title}>
                <div className="card h-100 border-0 shadow-sm landing-card reveal-up">
                  <div className="card-body p-4">
                    <IconBadge label={title} />
                    <h3 className="h5 fw-bold mt-3">{title}</h3>
                    <p className="text-secondary mb-0">{description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-5 bg-light" id="workflow">
        <div className="container py-4">
          <div className="row align-items-center g-4">
            <div className="col-lg-4">
              <p className="text-primary fw-semibold mb-2">How it works</p>
              <h2 className="fw-bold mb-3">A simple flow from order entry to billing</h2>
              <p className="text-secondary mb-0">
                Fintrix keeps each stage visible, so your team can move faster and clients get clearer updates.
              </p>
            </div>
            <div className="col-lg-8">
              <div className="row g-3">
                {steps.map(([title, description], index) => (
                  <div className="col-md-6" key={title}>
                    <div className="card border-0 shadow-sm landing-card h-100">
                      <div className="card-body p-4">
                        <span className="badge rounded-pill text-bg-primary mb-3">Step {index + 1}</span>
                        <h3 className="h5 fw-bold">{title}</h3>
                        <p className="text-secondary mb-0">{description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5" id="preview">
        <div className="container py-4">
          <div className="text-center mx-auto mb-5 section-heading">
            <p className="text-primary fw-semibold mb-2">Preview</p>
            <h2 className="fw-bold">Dashboard-style screens built for daily work</h2>
          </div>
          <div className="row g-4">
            {['Client Orders', 'Billing Summary', 'Progress Stages'].map((title) => (
              <div className="col-md-4" key={title}>
                <div className="preview-shot shadow-sm">
                  <div className="preview-topbar" />
                  <div className="preview-lines">
                    <span />
                    <span />
                    <span />
                  </div>
                  <div className="preview-chart" />
                  <h3 className="h6 fw-bold mb-0">{title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-5 bg-light" id="benefits">
        <div className="container py-4">
          <div className="row g-4 align-items-center">
            <div className="col-lg-5">
              <p className="text-primary fw-semibold mb-2">Benefits</p>
              <h2 className="fw-bold mb-3">Built to make business operations feel calmer</h2>
              <p className="text-secondary mb-0">
                Give your staff a reliable place to manage customer work, billing status, and order progress.
              </p>
            </div>
            <div className="col-lg-7">
              <div className="row g-3">
                {benefits.map(([title, description]) => (
                  <div className="col-sm-6" key={title}>
                    <div className="d-flex gap-3 p-3 bg-white rounded-4 shadow-sm h-100">
                      <IconBadge label={title} />
                      <div>
                        <h3 className="h6 fw-bold mb-1">{title}</h3>
                        <p className="text-secondary mb-0">{description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 landing-cta text-white">
        <div className="container py-4 text-center">
          <h2 className="fw-bold mb-3">Start managing your business smarter today</h2>
          <p className="lead mb-4">Bring billing, orders, clients, and progress tracking into one clean system.</p>
          <Link className="btn btn-light btn-lg px-4" to="/register">Try Fintrix Now</Link>
        </div>
      </section>

      <footer className="py-4 bg-dark text-white">
        <div className="container d-flex flex-column flex-md-row justify-content-between gap-3">
          <div>
            <h2 className="h5 fw-bold mb-1">Fintrix</h2>
            <p className="text-white-50 mb-0">Smart billing for growing businesses.</p>
          </div>
          <div className="d-flex flex-wrap gap-3">
            <a className="text-white-50" href="#features">Features</a>
            <a className="text-white-50" href="#workflow">How it works</a>
            <a className="text-white-50" href="mailto:hello@fintrix.example">hello@fintrix.example</a>
          </div>
          <p className="text-white-50 mb-0">Copyright 2026 Fintrix. All rights reserved.</p>
        </div>
      </footer>
    </main>
  );
}

export default LandingPage;
